import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import { ArrowBack, Fullscreen, FullscreenExit, ZoomIn, ZoomOut } from '@mui/icons-material';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

export interface ViewerItem {
  src: string;
  // Shown centred in the header bar.
  caption?: string;
  // Videos get the same shell but play inline instead of zooming.
  type?: 'image' | 'video';
}

interface MediaViewerProps {
  open: boolean;
  items: ViewerItem[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

/**
 * Full-screen media lightbox — zoom, drag-to-pan, wheel zoom, maximise, dot indicators.
 *
 * Lifted out of `pages/transactions/details.tsx`, which had it inline. Behaviour and
 * styling are a faithful port; the two differences are deliberate:
 *   - the backdrop is reached through a ref rather than `getElementById('viewerBackdrop')`,
 *     which also removes the MutationObserver that waited for that element to appear;
 *   - Escape and the arrow keys work.
 * Video items render a player in the same shell, with zoom hidden.
 */
const MediaViewer = ({ open, items, index, onIndexChange, onClose }: MediaViewerProps) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  // Mirrors of the above, so the native listeners below read current values without
  // being torn down and re-attached on every zoom/pan tick.
  const zoomLevelRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const lastMouse = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const current = items[index];
  const isVideo = current?.type === 'video';

  const resetTransform = () => {
    setZoomLevel(1);
    zoomLevelRef.current = 1;
    setPan({ x: 0, y: 0 });
    panRef.current = { x: 0, y: 0 };
    isPanningRef.current = false;
    setIsDragging(false);
  };

  const handleClose = () => {
    resetTransform();
    setIsMaximized(false);
    onClose();
  };

  const gotoNext = () => {
    resetTransform();
    onIndexChange((index + 1) % items.length);
  };

  const gotoPrev = () => {
    resetTransform();
    onIndexChange((index - 1 + items.length) % items.length);
  };

  // Reset zoom whenever the viewer opens or moves to a different item.
  useEffect(() => {
    if (open) resetTransform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index]);

  // Pan + wheel-zoom. Attached natively because the wheel handler must be non-passive
  // to call preventDefault, which React's synthetic onWheel cannot guarantee.
  useEffect(() => {
    const backdrop = backdropRef.current;
    if (!open || !backdrop || isVideo) return;

    const onMouseDown = (event: MouseEvent) => {
      if (zoomLevelRef.current <= MIN_ZOOM) return;
      isPanningRef.current = true;
      hasDraggedRef.current = false;
      setIsDragging(true);
      lastMouse.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = event.clientX - lastMouse.current.x;
      const dy = event.clientY - lastMouse.current.y;
      // Only count as a drag past 3px, so a shaky click still closes the viewer.
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasDraggedRef.current = true;
      lastMouse.current = { x: event.clientX, y: event.clientY };
      panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy };
      setPan({ ...panRef.current });
    };

    const onMouseUp = () => {
      isPanningRef.current = false;
      setIsDragging(false);
    };

    const onClickCapture = (event: MouseEvent) => {
      // Swallow the click that ends a drag, so panning never closes the viewer.
      if (hasDraggedRef.current) {
        event.stopPropagation();
        hasDraggedRef.current = false;
      }
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const delta = event.deltaY > 0 ? -0.2 : 0.2;
      setZoomLevel((prev) => {
        const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, prev + delta));
        if (next <= MIN_ZOOM) {
          panRef.current = { x: 0, y: 0 };
          setPan({ x: 0, y: 0 });
        }
        zoomLevelRef.current = next;
        return next;
      });
    };

    backdrop.addEventListener('mousedown', onMouseDown);
    backdrop.addEventListener('wheel', onWheel, { passive: false });
    backdrop.addEventListener('click', onClickCapture, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    return () => {
      backdrop.removeEventListener('mousedown', onMouseDown);
      backdrop.removeEventListener('wheel', onWheel);
      backdrop.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isVideo]);

  // Capture phase, and the event is swallowed once handled: the viewer can be opened
  // from inside a MUI Dialog, and in the bubble phase the Dialog would see the same
  // Escape and close itself out from under the viewer.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
      else if (event.key === 'ArrowRight' && items.length > 1) gotoNext();
      else if (event.key === 'ArrowLeft' && items.length > 1) gotoPrev();
      else return;
      event.stopPropagation();
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, items.length]);

  if (!open || !current) return null;

  const transform = `scale(${zoomLevel}) translate(${pan.x / zoomLevel}px, ${pan.y / zoomLevel}px)`;

  return (
    <Box
      ref={backdropRef}
      onClick={(event) => {
        if (event.target === event.currentTarget && !hasDraggedRef.current) handleClose();
      }}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        bgcolor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: zoomLevel > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default'
      }}
    >
      <Box
        sx={{
          width: isMaximized ? '100vw' : '90vw',
          maxWidth: isMaximized ? '100vw' : 900,
          height: isMaximized ? '100vh' : 'auto',
          maxHeight: isMaximized ? '100vh' : 'auto',
          bgcolor: 'rgba(20,20,20,0.95)',
          borderRadius: isMaximized ? 0 : 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: isMaximized ? 'none' : '0.5px solid rgba(255,255,255,0.12)',
          transition: 'all 0.2s ease-in-out'
        }}
      >
        {/* ── Header bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            borderBottom: '0.5px solid rgba(255,255,255,0.1)',
            flexShrink: 0
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.04em' }}>
            {index + 1} / {items.length}
          </Typography>

          <Typography
            sx={{
              color: 'rgba(255,255,255,0.85)',
              fontSize: '12px',
              fontWeight: 600,
              textAlign: 'center',
              letterSpacing: '0.02em',
              flex: 1,
              mx: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {current.caption || ''}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setIsMaximized((prev) => !prev)}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '0.5px solid rgba(255,255,255,0.15)',
                borderRadius: 1,
                width: 28,
                height: 28
              }}
            >
              {isMaximized ? <FullscreenExit sx={{ fontSize: 16 }} /> : <Fullscreen sx={{ fontSize: 16 }} />}
            </IconButton>

            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '0.5px solid rgba(255,255,255,0.18)',
                borderRadius: 1,
                width: 28,
                height: 28
              }}
            >
              <HighlightOffIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Media area ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: isMaximized ? 0 : '60vh',
            maxHeight: isMaximized ? 'none' : '80vh',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: !isVideo && zoomLevel > MIN_ZOOM ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
        >
          <Box
            component="figure"
            sx={{
              m: 0,
              overflow: 'visible',
              flex: 1,
              // Needs an explicit width and a zero min-height for the video below to have
              // something to be 100% *of*; a bare flex item would collapse to its content.
              width: '100%',
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isVideo ? (
              <video
                src={current.src}
                controls
                autoPlay
                style={{
                  // A <video> lays out at the source's intrinsic dimensions unless told
                  // otherwise, so a small file renders postage-stamp-sized however much
                  // room it has. Fill the shell instead and let object-fit letterbox it —
                  // there is no zoom control for video to compensate with.
                  width: '100%',
                  height: '100%',
                  maxHeight: isMaximized ? '82vh' : '68vh',
                  objectFit: 'contain',
                  display: 'block',
                  backgroundColor: '#000'
                }}
              />
            ) : (
              <img
                src={current.src}
                alt={current.caption || `Item ${index + 1}`}
                style={{
                  maxWidth: '85vw',
                  maxHeight: isMaximized ? '82vh' : '68vh',
                  objectFit: 'contain',
                  display: 'block',
                  backgroundColor: '#fff',
                  transform,
                  transition: isDragging ? 'none' : 'transform 0.15s ease-out',
                  transformOrigin: 'center center',
                  userSelect: 'none',
                  // Critical: at zoom the image must not swallow mousedown, or the
                  // backdrop never sees it and panning dies.
                  pointerEvents: zoomLevel > MIN_ZOOM ? 'none' : 'auto'
                }}
              />
            )}
          </Box>

          {items.length > 1 && (
            <>
              <IconButton
                onClick={gotoPrev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '0.5px solid rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ArrowBack />
              </IconButton>
              <IconButton
                onClick={gotoNext}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  border: '0.5px solid rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <ArrowBack sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            </>
          )}
        </Box>

        {/* ── Footer: dot indicators centred, zoom controls right ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.5,
            py: 1,
            borderTop: '0.5px solid rgba(255,255,255,0.1)',
            flexShrink: 0
          }}
        >
          {/* Mirrors the zoom controls' width so the dots sit truly centred. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, visibility: 'hidden' }}>
            <Box sx={{ width: 28 }} />
            <Box sx={{ width: 90 }} />
            <Box sx={{ width: 28 }} />
            <Box sx={{ width: 32 }} />
          </Box>

          {items.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {items.map((_item, i) => (
                <Box
                  key={i}
                  onClick={() => {
                    resetTransform();
                    onIndexChange(i);
                  }}
                  sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease' }}
                >
                  {i === index ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        bgcolor: '#1976d2',
                        borderRadius: '10px',
                        px: 1,
                        py: 0.25,
                        border: '1px solid rgba(25,118,210,0.6)'
                      }}
                    >
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0 }} />
                      <Typography sx={{ color: '#fff', fontSize: '10px', fontWeight: 600, lineHeight: 1 }}>{i + 1}</Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.3)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.6)' }
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, visibility: isVideo ? 'hidden' : 'visible' }}>
            <IconButton
              size="small"
              onClick={() => {
                const next = Math.max(MIN_ZOOM, zoomLevel - 0.5);
                setZoomLevel(next);
                zoomLevelRef.current = next;
                if (next <= MIN_ZOOM) {
                  panRef.current = { x: 0, y: 0 };
                  setPan({ x: 0, y: 0 });
                }
              }}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '0.5px solid rgba(255,255,255,0.15)',
                borderRadius: 1,
                width: 28,
                height: 28,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
              }}
            >
              <ZoomOut sx={{ fontSize: 16 }} />
            </IconButton>

            <Slider
              value={zoomLevel}
              onChange={(_event, val) => {
                const z = val as number;
                setZoomLevel(z);
                zoomLevelRef.current = z;
                if (z <= MIN_ZOOM) {
                  panRef.current = { x: 0, y: 0 };
                  setPan({ x: 0, y: 0 });
                }
              }}
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.1}
              sx={{
                width: 90,
                color: '#1976d2',
                '& .MuiSlider-thumb': { width: 12, height: 12 },
                '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.2)' }
              }}
            />

            <IconButton
              size="small"
              onClick={() => {
                const next = Math.min(MAX_ZOOM, zoomLevel + 0.5);
                setZoomLevel(next);
                zoomLevelRef.current = next;
              }}
              sx={{
                color: '#fff',
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '0.5px solid rgba(255,255,255,0.15)',
                borderRadius: 1,
                width: 28,
                height: 28,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' }
              }}
            >
              <ZoomIn sx={{ fontSize: 16 }} />
            </IconButton>

            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '11px', fontWeight: 500, minWidth: 32, textAlign: 'right' }}>
              {zoomLevel.toFixed(1)}x
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MediaViewer;
